const win = window;
const io = 'IntersectionObserver';
const supportsIO = io in win && `${io}Entry` in win;
const ric = 'requestIdleCallback';
const supportsRIC = ric in win;
const loading = 'loading';
const supportsImageLoading = loading in HTMLImageElement.prototype;
const supportsIframeLoading = loading in HTMLIFrameElement.prototype;
const userAgent = navigator.userAgent;
const isCrawler = /baidu|(?:google|bing|yandex|duckduck)bot/i.test(userAgent);
const isIE = /Trident/.test(userAgent);
const dataAttrs = ['srcset', 'src', 'poster'];
const dataPrefix = 'data-';
const lazyClass = 'lazy';
const query = `img.${lazyClass},video.${lazyClass},iframe.${lazyClass},source.${lazyClass}`;

let observer;

/**
 * A wrapper for the common use-case of looping through a list and applying
 * a function.
 *
 * @template T
 * @param {ArrayLike<T>} items
 * @param {(param: T) => void} fn
 * @return {void}
 */
function applyEach(items, fn) {
  for (let idx = 0; idx < items.length; idx++) {
    fn(items[idx]);
  }
}

/**
 * Flips all "data-" values to non "data-" values.
 *
 * @param {HTMLElement} element
 * @returns {void}
 */
function flipDataAttrs(element) {
  const dataset = element.dataset;

  for (let dataAttrIndex in dataAttrs) {
    const dataAttr = dataAttrs[dataAttrIndex];

    if (dataAttr in dataset) {
      element.setAttribute(dataAttr, dataset[dataAttr] || '');
      // console.log(element,dataAttr,dataset[dataAttr] || '');
      element.removeAttribute(`${dataPrefix}${dataAttr}`);

      const parentNode = element.parentNode;

      if (
        element.nodeName === 'SOURCE' && // is a <source> element
        parentNode instanceof HTMLVideoElement && // the parent element is a <video>
        parentNode.autoplay // and it has <video autoplay>
      ) {
        parentNode.load();

        // IE11 needs to have this method invoked in order for autoplay to start
        if (isIE) {
          parentNode.play();
        }

        parentNode.classList.remove(lazyClass);
      }

      element.classList.remove(lazyClass);
    }
  }
}

/**
 * Iterates through all "source" if <picture> or <video> and flips their "data-"
 * attributes or just flips the <img> or <iframe> attributes.
 *
 * @param {HTMLElement} element
 */
function load(element, carousel) {
  // const parentNode = element.parentNode;
  const parentNode = carousel ? element : element.parentNode;

  if (parentNode && parentNode.nodeName == 'PICTURE') {
    applyEach(parentNode.querySelectorAll('source'), flipDataAttrs);
  }

  if (element.nodeName == 'VIDEO') {
    applyEach(element.querySelectorAll('source'), flipDataAttrs);
  }

  flipDataAttrs(element);
}

function lazyload() {
  let elements = /** @type {HTMLElement[]} */ (
    Array.from(document.querySelectorAll(query))
  );
  // just stop here, we got nothing
  if (!elements.length) return;

  if (supportsIO && !isCrawler) {
    // we're in a non-bot browser with IntersectionObserver
    observer = new win[io](
      (entries) => {
        applyEach(entries, (entry) => {
          if (entry.isIntersecting || entry.intersectionRatio) {
            const element = /** @type {HTMLElement} */ (entry.target);

            if (supportsRIC) {
              // @ts-ignore: not gonna add a .d.ts for requestIdleCallback
              win[ric](
                () => {
                  load(element);
                },
                {
                  timeout: 200,
                }
              );
            } else {
              load(element);
            }

            observer.unobserve(element);
            elements = elements.filter((el) => el !== element);

            // if we've activated all available options, we're done here
            if (!elements.length) {
              observer.disconnect();
            }
          }
        });
      },
      { rootMargin: '200px 0%' }
    );

    applyEach(elements, (element) => {
      const nodeName = element.nodeName;

      if (
        (nodeName === 'IMG' && supportsImageLoading) ||
        (nodeName === 'IFRAME' && supportsIframeLoading)
      ) {
        element.setAttribute('loading', 'lazy');
        flipDataAttrs(element);
        elements = elements.filter((el) => el !== element);
        return;
      } else {
        observer.observe(element);
      }
    });

    // everything was supported by loading=lazy
    if (!elements.length) {
      observer.disconnect();
    }
  } else {
    // we're in a bot browser or browser without IntersectionObserver
    applyEach(elements, load);
  }
}

export { load, lazyload, observer };
